import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Card, Pagination, Typography, useRouteDocumentTitle, exportToExcel } from '@client/toolkit';
import { FaCalendarAlt } from 'react-icons/fa';
import { ReportingPaths } from '../reporting.router';
import { BuildingRentalTable } from './components/building-rental-table';
import { OutstandingValueCard } from '../components/outstanding-value-card';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { DateRangeFilter, notSameYear } from '../components/date-range-filter';
import { dateRangeOptions } from '../utils/date-range-options';
import {
  useGetRentalIncomesDetailsQuery,
  useGetRentalIncomesSummaryQuery,
  useLazyGetRentalIncomesExportDetailsQuery,
} from '@client/api';
import dayjs from 'dayjs';
import { transformToExcelFormat } from './utils/transformToExcelFormat';
import { SortDirection } from '../components/expanable-table-wraper';

export function ReportingRentalIncomeRoute() {
  const { t, i18n } = useTranslation('reporting');
  const [searchParams] = useSearchParams();
  useRouteDocumentTitle(t('reporting.documentTitle'), ReportingPaths.RentalIncome);

  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');

  const initialStartDate = startDateParam ? dayjs(startDateParam).toDate() : new Date(new Date().getFullYear(), 0, 1);
  const initialEndDate = endDateParam ? dayjs(endDateParam).toDate() : new Date(new Date().getFullYear(), 11, 31);

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [timeRange, setTimeRange] = useState<{ startDate: Date; endDate: Date } | null>(null);

  const displayStartDate = timeRange?.startDate ?? initialStartDate;
  const displayEndDate = timeRange?.endDate ?? initialEndDate;
  const [page, setPage] = useState(0);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(undefined);
  const [exporting, setExporting] = useState(false);

  const { data, isFetching } = useGetRentalIncomesSummaryQuery(
    {
      startDate: dayjs(timeRange?.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange?.endDate).format('YYYY-MM-DD'),
    },
    {
      skip: !timeRange,
    },
  );
  const { data: detailsData, isFetching: isFetchingDetails } = useGetRentalIncomesDetailsQuery(
    {
      startDate: dayjs(timeRange?.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange?.endDate).format('YYYY-MM-DD'),
      orderBy: sortColumn && sortDirection ? `${sortColumn}:${sortDirection}` : undefined,
      page: page,
      pageSize: 10,
    },
    {
      skip: !timeRange,
    },
  );

  const [trigger] = useLazyGetRentalIncomesExportDetailsQuery();
  const exportExcel = useCallback(async () => {
    if (!timeRange) return;
    setExporting(true);
    const result = await trigger({
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
      pageSize: 0,
    });
    if (result.isSuccess && result.data) {
      exportToExcel(
        transformToExcelFormat(
          result.data,
          selectedRows,
          timeRange.startDate.getFullYear(),
          timeRange.startDate.getFullYear() - 1,
        ),
        'report-rental-income.xlsx',
      );
    }
    setExporting(false);
  }, [selectedRows, timeRange, trigger]);

  return (
    <ReportPageContent
      breadcrumb={t('navigation.reporting.rentalIncome')}
      menu={
        <DateRangeFilter
          options={[dateRangeOptions().currentVsPreviousYear, dateRangeOptions().custom]}
          onChange={(d) => setTimeRange({ startDate: d[0], endDate: d[1] })}
          shouldDisableDate={notSameYear}
          initialStartDate={initialStartDate}
          initialEndDate={initialEndDate}
          onExportExcel={exportExcel}
          exporting={exporting}
        />
      }
    >
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <OutstandingValueCard
          title={t('rentalIncome', { year: displayStartDate.getFullYear() })}
          amount={data?.currentPeriodIncome}
          isLoading={isFetching}
        />
        <OutstandingValueCard
          title={t('rentalIncome', { year: displayStartDate.getFullYear() - 1 })}
          amount={data?.comparisonPeriodIncome}
          isLoading={isFetching}
        />
        <OutstandingValueCard
          title={t('development')}
          amount={data?.variance.absolute}
          showChange
          changeValue={data?.variance.percentage}
          isLoading={isFetching}
        />
      </div>
      <div>
        <span className="flex items-baseline gap-2 mb-4 text-sm">
          <FaCalendarAlt className="text-white" />
          <Typography className="text-white text-sm">
            {t('dateRangeBaseVsComparison', {
              baseStartDate: dayjs(displayStartDate).subtract(1, 'year').toDate().toLocaleDateString(i18n.language),
              baseEndDate: dayjs(displayEndDate).subtract(1, 'year').toDate().toLocaleDateString(i18n.language),
              comparisonStartDate: displayStartDate.toLocaleDateString(i18n.language),
              comparisonEndDate: displayEndDate.toLocaleDateString(i18n.language),
            })}
          </Typography>
        </span>
        <Card className="pb-4">
          <BuildingRentalTable
            data={detailsData?.items ?? []}
            baseYear={displayStartDate.getFullYear()}
            comparisonYear={displayStartDate.getFullYear() - 1}
            loading={isFetchingDetails}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={(col, direction) => {
              setSortColumn(col);
              setSortDirection(direction);
              setPage(0);
            }}
            onSelected={(ids) => {
              setSelectedRows(ids);
            }}
          />
          <Pagination
            totalCount={detailsData?.total ?? 0}
            currentPage={page}
            setPage={setPage}
            pageSize={detailsData?.pageSize ?? 10}
            visible={true}
          />
        </Card>
      </div>
    </ReportPageContent>
  );
}
